/* eslint-disable eqeqeq */
import React, { useState } from "react";
import { Formik, Form } from "formik";
import { useQueryClient } from "react-query";
import { useSelector, useDispatch } from "react-redux";
import { Accordion, AccordionTab } from "primereact/accordion";
import {
  selectToken,
  handleInvalidToken,
  handleAPIError,
} from "../../../../../utils/auth/authSlice";

// custom components
import PageWrapper from "../../../../PageWrapper";
import Label from "../../../../Label";
import PrimaryButton from "../../../../SFUCentralThemedComponents/PrimaryButton";
import FileUpload from "../../../../SFUCentralThemedComponents/FileUpload";
import AutoSave from "../../../../../utils/formik/AutoSave";
import Editor from "../../../../GenericFormikComponents/Editor";

import ProgressSpinnerPR from "../../../../PrimeReact/ProgressSpinnerPR";
import DialogPR from "../../../../PrimeReact/DialogPR";
import { validationSchema, initialValues } from "./validationSchema";

import { useDeleteScholarshipAppDoc } from "../../../../../hooks/ScholarshipApp/useDeleteScholarshipAppDoc";

export default function ReferenceDocPage({
  goToPage,
  scholarshipAppData,
  scholarshipAppOptions,
  saveField,
  saveFieldStatus,
  savePage,
  pageInfo,
  steppers,
  spinnerStatus,
}) {
  const [formikInitialErrors, setFormikInitialErrors] = useState({});
  const [formikInitialTouched, setFormikInitialTouched] = useState({});
  const [uploadButtonDisabled, setUploadButtonDisabled] = useState(false);
  const [lastSaved, setLastSaved] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const [activeIndex, setActiveIndex] = useState();

  const queryClient = useQueryClient();

  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const {
    mutate: deleteDoc,
    isLoading: isDeleteDocLoading,
  } = useDeleteScholarshipAppDoc(
    scholarshipAppData.data.scholarship_id,
    token,
    () => {
      dispatch(handleInvalidToken());
    },
    () => {
      dispatch(handleAPIError());
    }
  );

  const handleOnUpload = (formikErrors, formikTouched) => {
    // remove the docs errors if set
    const { docs, ...updatedFormikErrors } = formikErrors;

    // save error and touched states
    setFormikInitialErrors(updatedFormikErrors);
    setFormikInitialTouched(formikTouched);

    // update formik initial values by updating query cache
    queryClient.invalidateQueries("scholarshipApp");

    setUploadButtonDisabled(false);
    setIsUploading(false);
  };

  const handleFileDelete = (e) => {
    deleteDoc({ scholarship_application_docs_id: e.target.value });
  };

  let referenceTypeId = 1;
  let minReferenceDocs = 0;
  let maxReferenceDocs = 10;
  scholarshipAppOptions.data.doc_requirements.forEach((val) => {
    if (val.type === "reference") {
      referenceTypeId = val.scholarship_doc_types_id;
      minReferenceDocs = val.min;
      maxReferenceDocs = val.max;
    }
  });

  const handleBeforeNewUpload = (request, docType, formik) => {
    setUploadButtonDisabled(true);
    setIsUploading(true);
    request.formData.append(
      "scholarshipAppId",
      scholarshipAppData.data.scholarship_application_id
    );
    request.formData.append(
      "scholarshipId",
      scholarshipAppData.data.scholarship_id
    );
    request.formData.append("scholarshipDocTypesId", referenceTypeId);
    request.formData.append("docType", docType);
    savePage(formik.values);
  };

  const docsFiltered = scholarshipAppData.data.docs
    ? scholarshipAppData.data.docs.filter(
        (doc) => doc.scholarship_doc_types_id == referenceTypeId
      )
    : [];

  const getInitialWordCounts = () => {
    return docsFiltered.map(function (doc) {
      if (
        !scholarshipAppData.data[
          `docNote_${doc.scholarship_application_docs_id}`
        ] ||
        !scholarshipAppData.data[
          `docNote_${doc.scholarship_application_docs_id}`
        ].replace(/<\/?[^>]+(>)/g, " ") ||
        !scholarshipAppData.data[
          `docNote_${doc.scholarship_application_docs_id}`
        ]
          .replace(/<\/?[^>]+(>)/g, " ")
          .match(/\S+/g)
      ) {
        return 0;
      }
      return scholarshipAppData.data[
        `docNote_${doc.scholarship_application_docs_id}`
      ]
        .replace(/<\/?[^>]+(>)/g, " ")
        .match(/\S+/g).length;
    });
  };

  const wordCounts = getInitialWordCounts();
  const [wordCount, setWordCount] = useState(wordCounts);

  const docs = () => {
    if (!scholarshipAppData.data.docs) {
      return "";
    }
    let docNum = 0;
    const formattedDocsFiltered = docsFiltered.map((doc, index) => {
      const formattedDocComponent = (
        <>
          <div key={doc.scholarship_application_docs_id} className="p-grid">
            <div
              key={`docNoteLabel_${doc.scholarship_application_docs_id}`}
              className="p-col-12"
            >
              <h3>Letter of Support {(docNum += 1)}</h3>
              <span>File name: {doc.display_file_name}</span>

              <div style={{ marginTop: "2px" }}>
                <PrimaryButton
                  type="button"
                  value={doc.scholarship_application_docs_id}
                  onClick={handleFileDelete}
                  className="small"
                >
                  Delete
                </PrimaryButton>
                <PrimaryButton
                  type="button"
                  onClick={() => window.open(doc.download_link)}
                  className="small"
                >
                  Download
                </PrimaryButton>
              </div>
            </div>
            <div
              key={`docNoteDiv_${doc.scholarship_application_docs_id}`}
              className="p-col-12"
            >
              <Label>
                <span>
                  Please describe why you chose this document (50 words max)
                </span>
              </Label>
              <div className="p-grid">
                <div className="p-col-12 p-md-6">
                  <Editor
                    headerTemplate={<span />}
                    name={`docNote_${doc.scholarship_application_docs_id}`}
                    key={`docNote_${doc.scholarship_application_docs_id}`}
                    style={{ height: "155px" }}
                    formats={[]}
                    onTextChangeCustom={(e) => {
                      const wordCountCopy = [...wordCount];
                      wordCountCopy[index] = e.textValue.match(/\S+/g)
                        ? e.textValue.match(/\S+/g).length
                        : 0;
                      setWordCount(wordCountCopy);
                    }}
                  />
                  <span className="push-right word-limit-count">
                    {wordCount[index]} / 50 Words
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      );
      return formattedDocComponent;
    });

    return formattedDocsFiltered;
  };

  const existingDocs = scholarshipAppData.data.docs
    ? scholarshipAppData.data.docs
    : [];

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const nth = (d) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
      case 1:  return "st";
      case 2:  return "nd";
      case 3:  return "rd";
      default: return "th";
    }
  };

  let application_end_date_obj = new Date(scholarshipAppData.data.application_end_date);
  let day = application_end_date_obj.getDate();
  let month = monthNames[application_end_date_obj.getMonth()];
  let year = application_end_date_obj.getFullYear();
  let ordinal = nth(day);

  // Display the upload button if # of docs with docType existing is < maxDocs
  const renderNewUploadButton = (formik, docType, maxDocs) => {
    if (!scholarshipAppData.data.docs || docsFiltered.length < maxDocs) {
      return (
        <div className="p-col-12">
          <FileUpload
            name="docUpload[]"
            url={`${process.env.REACT_APP_API_URL}tracs_v3/scholarships/Scholarship_application_protected_api/uploadDoc`}
            onUpload={() => handleOnUpload(formik.errors, formik.touched)}
            accept=".pdf, .doc, .docx, .jpeg, .jpg, .png, .gif"
            mode="basic"
            onBeforeUpload={(request) => {
              handleBeforeNewUpload(request, docType, formik);
            }}
            onBeforeSend={(request) =>
              request.xhr.setRequestHeader("Authorization", `Basic ${token}`)
            }
            onError={() => {
              setUploadButtonDisabled(false);
              setIsUploading(false);
              setIsErrorDialogOpen(true);
            }}
            auto
            chooseLabel="Upload Letter of Support"
            disabled={uploadButtonDisabled}
          />
          <br />
          <br />
          <Accordion
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
          >
            <AccordionTab header="Unable to upload a letter of support? Click here">
              <p>
                If, due to school policy, you are{" "}
                <span className="heavy">unable</span> to upload your letter of
                support, please upload a document that includes the following
                information:
                <ol>
                  <li> Your First Name, Last Name and SFU ID </li>
                  <li>
                    {" "}
                    First Name and Last Name of the supporter who is providing a
                    letter of support for you{" "}
                  </li>
                  <li> Email address of your supporter </li>
                  <li>
                    {" "}
                    A brief description of your relationship with this supporter
                    and how you know them
                  </li>
                </ol>
                <h4>
                  Please ask your supporter to email the letter of support to
                  sfu_uggla_scholars@sfu.ca by {month} {day}{ordinal}, {year} at 23:59 PST,
                  with your name and SFU ID clearly identified.
                </h4>
              </p>
            </AccordionTab>
          </Accordion>
          {/* <PrimaryButton
              type="button"
              onClick={handleFileDelete}
              className="small p-button-raised p-button-info"
            >
              Unable to Upload Letter of Support? Click here
            </PrimaryButton> */}
        </div>
      );
    }

    return (
      <div className="p-col-12">
        <Accordion
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
        >
          <AccordionTab header="Unable to upload a letter of support? Click here">
            <p>
              If, due to school policy, you are{" "}
              <span className="heavy">unable</span> to upload your letter of
              support, please upload a document that includes the following
              information:
              <ol>
                <li> Your First Name, Last Name and SFU ID </li>
                <li>
                  {" "}
                  First Name and Last Name of the supporter who is providing a
                  letter of support for you{" "}
                </li>
                <li> Email address of your supporter </li>
                <li>
                  {" "}
                  A brief description of your relationship with this supporter
                  and how you know them
                </li>
              </ol>
              <h4>
                Please ask your supporter to email the letter of support to
                sfu_uggla_scholars@sfu.ca by {month} {day}{ordinal}, {year} at 23:59 PST,
                with your name and SFU ID clearly identified.
              </h4>
            </p>
          </AccordionTab>
        </Accordion>
      </div>
    );
  };

  const errorDialogFooter = (
    <div>
      <PrimaryButton type="button" onClick={() => setIsErrorDialogOpen(false)}>
        {" "}
        Okay{" "}
      </PrimaryButton>
    </div>
  );

  const errorDialogHeader = (
    <div style={{ color: "red" }}>File Upload Error</div>
  );

  const minMaxString =
    minReferenceDocs == maxReferenceDocs
      ? minReferenceDocs
      : `between ${minReferenceDocs} and ${maxReferenceDocs}`;

  return (
    <PageWrapper>
      <DialogPR
        header={errorDialogHeader}
        footer={errorDialogFooter}
        visible={isErrorDialogOpen}
        onHide={() => setIsErrorDialogOpen(false)}
      >
        {" "}
        There was an error during file upload. Please confirm that your file is
        a PDF, JPG, or DOC and smaller than 15MB
      </DialogPR>
      {steppers(pageInfo.pageNum)}

      <Formik
        initialValues={
          scholarshipAppData.isLoading
            ? initialValues(existingDocs)
            : scholarshipAppData.data
        } // display initial values if no page data exists
        initialErrors={formikInitialErrors}
        initialTouched={formikInitialTouched}
        validationSchema={validationSchema(
          existingDocs,
          referenceTypeId,
          minReferenceDocs,
          maxReferenceDocs
        )} // validation schema is defined at the top of the page
        onSubmit={(values) => {
          savePage(values);
          goToPage(pageInfo.pageNum + 1);
          window.scrollTo(0, 0);
        }}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
      >
        {(formik) => (
          <Form>
            {!spinnerStatus ? (
              <AutoSave
                savedFields={Object.keys(initialValues(existingDocs))}
                saveField={saveField}
                saveFieldStatus={saveFieldStatus}
                setLastSaved={setLastSaved}
              />
            ) : (
              ""
            )}
            <div className="p-grid">
              <div className="p-col-12">
                <h1>Letters of Support</h1>
              </div>
              <div className="p-col-12">
                <p>
                  You are asked to provide two ({minMaxString}) letters of
                  support for your application. These letters of support must be
                  completed by someone who has direct knowledge of your personal
                  experiences, academic potential, achievements, or barriers to
                  post-secondary education that you describe in your
                  application.
                </p>

                <p>
                  In lieu of one (1) letter of support, you may choose to
                  provide a document as evidence of your personal experience or
                  achievements. Acceptable supporting documentation may include
                  but is not limited to: certificate of appreciation, media
                  story, or community publication. Please provide a brief
                  rationale as to why you chose this person or document to
                  support your application.
                </p>

                <p style={{ color: "green" }}>
                  Accepted file types are: PDF, JPG, or DOC with a maximum file
                  size of 15 MB.{" "}
                </p>
                {docs()}
              </div>

              {renderNewUploadButton(formik, "reference", maxReferenceDocs)}
              {formik.errors?.docs ? (
                <div>
                  <small className="p-invalid">{formik.errors.docs}</small>
                </div>
              ) : (
                ""
              )}
              <div className="p-col-12" style={{ paddingTop: "0" }}>
                <span className="last-saved">
                  <em>{lastSaved}</em>
                </span>
              </div>
            </div>

            <div className="p-grid button-navigation-container">
              <div className="p-col-12">
                <PrimaryButton
                  type="button"
                  disabled={spinnerStatus}
                  onClick={() => {
                    savePage(formik.values);
                    goToPage(pageInfo.pageNum - 1);
                  }}
                >
                  Back
                </PrimaryButton>
                <PrimaryButton
                  type="submit"
                  className="push-right"
                  disabled={spinnerStatus}
                >
                  Next
                </PrimaryButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      {spinnerStatus || isDeleteDocLoading || isUploading ? (
        <ProgressSpinnerPR />
      ) : (
        ""
      )}
    </PageWrapper>
  );
}
